import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { gatewayWrite } from "@/lib/gatewayClient";

interface Profile {
  id: string;
  organization_id: string | null;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  job_title: string | null;
  department: string | null;
  phone: string | null;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string | null;
  ai_enabled: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  organization: Organization | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string, organizationName: string) => Promise<{ error: Error | null }>;
  signUpWithInvite: (email: string, password: string, fullName: string, inviteToken: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Defer Supabase calls with setTimeout to prevent deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchUserData(session.user.id);
          }, 0);
        } else {
          // User logged out - clean up presence channels
          cleanupPresence();
          setProfile(null);
          setOrganization(null);
          setIsAdmin(false);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserData(session.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const cleanupPresence = async () => {
    try {
      // Remove all realtime channels to clean up presence immediately
      const channels = supabase.getChannels();
      for (const channel of channels) {
        await supabase.removeChannel(channel);
      }
    } catch (error) {
      console.error("Error cleaning up presence:", error);
    }
  };

  const fetchUserData = async (userId: string) => {
    try {
      // Fetch profile
      const { data: profileData } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (profileData) {
        setProfile(profileData as Profile);

        // Fetch organization
        if (profileData.organization_id) {
          const { data: orgData } = await supabase
            .from("organizations")
            .select("*")
            .eq("id", profileData.organization_id)
            .single();

          if (orgData) {
            setOrganization(orgData as Organization);
          }
        }

        // Check admin role
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .eq("role", "admin")
          .single();

        setIsAdmin(!!roleData);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });

    // Audit trail: record the outcome server-side (best effort, never blocks sign-in).
    void supabase.functions
      .invoke("auth-event-log", {
        body: {
          event_type: error ? "login_failure" : "login_success",
          email,
          reason: error?.message,
        },
      })
      .catch(() => undefined);

    return { error };
  };


  const signUp = async (email: string, password: string, fullName: string, organizationName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          organization_name: organizationName,
        },
      },
    });
    return { error };
  };

  const signUpWithInvite = async (email: string, password: string, fullName: string, inviteToken: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    // First, get the invitation details
    const { data: invitation, error: inviteError } = await supabase
      .from("invitations")
      .select("*")
      .eq("token", inviteToken)
      .is("accepted_at", null)
      .single();

    if (inviteError || !invitation) {
      return { error: new Error("Invalid or expired invitation") };
    }

    // Check if expired
    if (new Date(invitation.expires_at) < new Date()) {
      return { error: new Error("This invitation has expired") };
    }

    // Create the user account
    const { data: authData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          invite_token: inviteToken,
          organization_id: invitation.organization_id,
          role: invitation.role,
        },
      },
    });

    if (signUpError) {
      return { error: signUpError };
    }

    // If signup successful, update the user's profile and role
    if (authData.user) {
      try {
        // Update profile to join the invited organization
        await supabase
          .from("profiles")
          .update({
            organization_id: invitation.organization_id,
            full_name: fullName,
          })
          .eq("id", authData.user.id);

        // Add user role via gateway
        await gatewayWrite({
          table: "user_roles",
          operation: "insert",
          data: {
            user_id: authData.user.id,
            organization_id: invitation.organization_id,
            role: invitation.role,
          },
        });

        // Mark invitation as accepted
        await supabase
          .from("invitations")
          .update({ accepted_at: new Date().toISOString() })
          .eq("id", invitation.id);

      } catch (error) {
        console.error("Error setting up invited user:", error);
      }
    }

    return { error: null };
  };

  const signOut = async () => {
    // Clean up presence before signing out
    await cleanupPresence();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        organization,
        isAdmin,
        isLoading,
        signIn,
        signUp,
        signUpWithInvite,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

